import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { reduxForm } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';

import { Scrollbars } from 'react-custom-scrollbars';

import PageTitle from 'components/common/PageTitle';
import Button from 'components/common/Button';
import IconMenu from 'components/icons/IconMenu';

import PrintNavigationItem from './PrintNavigationItem';

const styles = theme => ({
  container: {
    height: '100%',
  },
  drawerPaper: {
    top: 64,
    width: 290,
    border: 'none',
    height: 'calc(100vh - 64px)',
    height: 'calc(100dvh - 64px)',
    background: theme.palette.common.white,
    overflow: 'visible',
    transition: theme.transitions.create(['width'], {
      duration: theme.transitions.duration.short,
    }),
    [theme.breakpoints.down('sm')]: {
      zIndex: 1200,
    },
  },
  drawerPaperCollapsed: {
    width: 0,
  },
  title: {
    position: 'relative',
    padding: '30px 40px 20px 30px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  sectionTitle: {
    padding: '10px 0 10px 0',
    margin: '0 30px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    '&:first-child': {
      marginTop: 12,
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px',
      width: 274,
    },
  },
  list: {
    padding: '24px 0 8px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: 290,
    },
  },
  buttonSign: {
    position: 'absolute',
    right: 0,
    top: 19,
    color: theme.palette.primary[600],
  },
  iconSignature: {
    height: 18,
  },
  subtitle: {
    padding: '0 40px 0 30px',
    [theme.breakpoints.down('sm')]: {
      padding: '0 8px',
    },
  },
  button: {
    display: 'none',
    position: 'absolute',
    zIndex: 10,
    width: 48,
    height: 48,
    top: -57,
    right: 'auto',
    left: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  iconMenu: {
    width: 24,
    height: 24,
    marginTop: 7,
  },
});

class PrintNavigation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autoHeightMax: 500,
    };

    this.onScrollbarsUpdate = this.onScrollbarsUpdate.bind(this);
  }

  componentDidMount() {
    this.onScrollbarsUpdate();
    window.addEventListener('resize', this.onScrollbarsUpdate);
  }

  componentDidUpdate(prevProps) {
    const { titleText, sections, expanded } = this.props;
    if (titleText !== prevProps.titleText
      || (prevProps.sections === null && sections !== null)
      || expanded !== prevProps.expanded) {
      this.onScrollbarsUpdate();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onScrollbarsUpdate);
  }

  onScrollbarsUpdate() {
    const titleHeight = this.titleRef
      ? this.titleRef.getBoundingClientRect().height : 0;
    const containerHeight = this.containerRef
      ? this.containerRef.getBoundingClientRect().height : 0;
    this.setState({ autoHeightMax: containerHeight - titleHeight });
  }

  render() {
    const {
      sections,
      titleText,
      titleHref,
      subtitle,
      expanded,
      onExpandCollapseButtonClick,
      classes,
    } = this.props;
    const { autoHeightMax } = this.state;
    if (!sections) {
      return '';
    }
    return (
      <Drawer
        open
        elevation={2}
        variant="permanent"
        classes={{
          paper: classNames(classes.drawerPaper, {
            [classes.drawerPaperCollapsed]: !expanded,
          }),
        }}
      >
        <Button
          variant="fab"
          className={classes.button}
          onClick={onExpandCollapseButtonClick}
        >
          <IconMenu className={classes.iconMenu} />
        </Button>
        <div
          className={classes.container}
          ref={(containerRef) => {
            this.containerRef = containerRef;
          }}
        >
          <div
            ref={(titleRef) => {
              this.titleRef = titleRef;
            }}
          >
            {titleText &&
              <PageTitle
                className={classes.title}
                text={titleText}
                {...titleHref ? { href: titleHref } : {}}
              />
            }
            {subtitle ? (
              <div className={classes.subtitle}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <Scrollbars
            style={{ height: `${autoHeightMax}px)` }}
            autoHeight
            autoHeightMin={autoHeightMax}
            autoHeightMax={autoHeightMax}
            thumbMinSize={30}
            universal
          >
            {sections.map(({ title, items }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {title &&
                  <Typography
                    variant="subheading"
                    className={classes.sectionTitle}
                  >
                    {title}
                  </Typography>
                }
                <List
                  className={classes.list}
                >
                  {items.map(item => (
                    <PrintNavigationItem
                      key={item.search ? `${item.href}?${item.search}` : item.href}
                      selectable={sections.length > 1 || items.length > 1}
                      {...item}
                    />
                  ))}
                </List>
              </Fragment>
            ))}
          </Scrollbars>
        </div>
      </Drawer>
    );
  }
}

PrintNavigation.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.string,
    })),
  })),
  titleText: PropTypes.string,
  titleHref: PropTypes.string,
  subtitle: PropTypes.node,
  expanded: PropTypes.bool.isRequired,
  onExpandCollapseButtonClick: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    drawerPaper: PropTypes.string.isRequired,
    drawerPaperCollapsed: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    sectionTitle: PropTypes.string.isRequired,
    list: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    iconMenu: PropTypes.string.isRequired,
  }).isRequired,
};

PrintNavigation.defaultProps = {
  sections: null,
  titleText: null,
  titleHref: null,
  subtitle: null,
};

export default reduxForm({
  form: 'printNavigationForm',
  destroyOnUnmount: false,
})(withStyles(styles)(PrintNavigation));


// WEBPACK FOOTER //
// ./src/components/common/Layout/PrintNavigation/PrintNavigation.js